"use client";

import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  NumberInput,
  PrintButton,
  PrintFooter,
  TableScroll,
  trackToolResult,
} from "@/components/tools/ui";

/* What RAMS paperwork costs over a year, from the numbers the user already
   knows about their own process. No invented savings or acceptance stats —
   we only add up the user's own inputs.

   Annual volume      = docs/month × 12
   In-house written   = volume × (1 − consultant share)
   Consultant written = volume × consultant share
   Time cost          = in-house written × hours each × hourly rate
   Consultant cost    = consultant written × consultant fee
   Send-back cost     = volume × send-back rate × hours lost × hourly rate
   Effective per-RAMS = total / volume */

const GBP = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

const HOURS = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 });

/** Parse a NumberInput string to a finite number, clamped to >= 0. */
function num(v: string, fallback = 0): number {
  const n = parseFloat(v);
  return isFinite(n) && n >= 0 ? n : fallback;
}

/** Clamp a percentage input to 0–100. */
function pct(v: string): number {
  return Math.min(100, num(v));
}

/** Hours lost to send-backs across a year, shared by every scenario. */
function sbCountToHours(volume: number, sbRate: number, sbHours: number): number {
  return volume * sbRate * sbHours;
}

/** £ cost of those send-back hours at the user's own rate. */
function c_sendBackCostBase(volume: number, sbRate: number, sbHours: number, rate: number): number {
  return sbCountToHours(volume, sbRate, sbHours) * rate;
}

export function RamsCostCalculator() {
  const [docsPerMonth, setDocsPerMonth] = useState("4");
  const [hoursEach, setHoursEach] = useState("3");
  const [hourlyRate, setHourlyRate] = useState("45");
  const [consultantShare, setConsultantShare] = useState("0");
  const [consultantFee, setConsultantFee] = useState("150");
  const [sendBackRate, setSendBackRate] = useState("20");
  const [sendBackHours, setSendBackHours] = useState("4");
  // Scenario-comparison inputs (all editable; defaults are typical UK figures).
  const [compareFee, setCompareFee] = useState("350");
  const [reviewMins, setReviewMins] = useState("15");
  const resultTracked = useRef(false);

  const c = useMemo(() => {
    const volume = num(docsPerMonth) * 12;
    const each = num(hoursEach);
    const rate = num(hourlyRate);
    const cShare = pct(consultantShare) / 100;
    const cFee = num(consultantFee);
    const sbRate = pct(sendBackRate) / 100;
    const sbHours = num(sendBackHours);

    const inHouseDocs = volume * (1 - cShare);
    const consultantDocs = volume * cShare;

    const writeHours = inHouseDocs * each;
    const sendBackCount = volume * sbRate;
    const sendBackHoursTotal = sendBackCount * sbHours;
    const totalHours = writeHours + sendBackHoursTotal;

    const timeCost = writeHours * rate;
    const consultantCost = consultantDocs * cFee;
    const sendBackCost = sendBackHoursTotal * rate;
    const totalCost = timeCost + consultantCost + sendBackCost;

    const perRams = volume > 0 ? totalCost / volume : 0;

    /* Scenario comparison — same volume, three ways of producing the RAMS.
       Each scenario carries the same send-back exposure unless a route changes
       it; we keep the user's own send-back rate across all three rather than
       inventing a quality uplift, so the table stays honest. */
    const cmpFee = num(compareFee);
    const reviewHrs = num(reviewMins) / 60;
    const sbCostPerYear = c_sendBackCostBase(volume, sbRate, sbHours, rate);

    // Doing it yourself: write every RAMS in-house at your own rate.
    const diyHours = volume * each + sbCountToHours(volume, sbRate, sbHours);
    const diyCost = volume * each * rate + sbCostPerYear;

    // Consultant: outsource every RAMS at a per-doc fee; little of your time.
    const consHours = sbCountToHours(volume, sbRate, sbHours);
    const consCost = volume * cmpFee + sbCostPerYear;

    // RamsDocs: a short competent-person review per RAMS, £0 during early access.
    const ramsHours = volume * reviewHrs + sbCountToHours(volume, sbRate, sbHours);
    const ramsCost = volume * reviewHrs * rate + sbCostPerYear; // £0 software during early access

    return {
      volume,
      totalHours,
      timeCost,
      consultantCost,
      sendBackCost,
      sendBackCount,
      totalCost,
      perRams,
      hasConsultant: consultantDocs > 0,
      hasSendBacks: sendBackCount > 0,
      rate,
      sbCostPerYear,
      scenarios: {
        diy: { hours: diyHours, cost: diyCost },
        consultant: { hours: consHours, cost: consCost },
        ramsdocs: { hours: ramsHours, cost: ramsCost },
      },
      saveVsConsultant: consCost - ramsCost,
      hoursVsConsultant: consHours - ramsHours,
    };
  }, [docsPerMonth, hoursEach, hourlyRate, consultantShare, consultantFee, sendBackRate, sendBackHours, compareFee, reviewMins]);

  // Fire the funnel event once per visit when a meaningful figure first appears.
  useEffect(() => {
    if (c.totalCost > 0 && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("rams-cost-calculator", { annualCost: Math.round(c.totalCost) });
    }
  }, [c.totalCost]);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      {/* Inputs */}
      <div className="card shadow-soft p-6">
        <h2 className="text-xl">Your RAMS process</h2>
        <p className="mt-1 text-sm text-muted">
          Use round numbers from a typical month — the calculator scales them to a year. Everything stays in
          your browser.
        </p>

        <div className="mt-5 space-y-5">
          <div className="rounded-xl border border-line bg-mist/60 p-4">
            <span className="text-[0.8rem] font-bold uppercase tracking-wide text-muted">Volume &amp; your time</span>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="RAMS needed per month">
                <NumberInput min={0} step={1} value={docsPerMonth} onChange={(e) => setDocsPerMonth(e.target.value)} placeholder="4" />
              </Field>
              <Field label="Hours to write each one yourself">
                <NumberInput min={0} step={0.5} value={hoursEach} onChange={(e) => setHoursEach(e.target.value)} placeholder="3" />
              </Field>
              <Field label="Your charge-out / hourly rate (£)" hint="What an hour of your time is worth — billable or owner time." className="sm:col-span-2">
                <NumberInput min={0} step={5} value={hourlyRate} onChange={(e) => setHourlyRate(e.target.value)} placeholder="45" />
              </Field>
            </div>
          </div>

          <div className="rounded-xl border border-line bg-mist/60 p-4">
            <span className="text-[0.8rem] font-bold uppercase tracking-wide text-muted">Consultant</span>
            <p className="mt-1 text-[0.78rem] text-muted">Leave at 0% if you write all your RAMS yourself.</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="% sent to a consultant instead">
                <NumberInput min={0} max={100} step={5} value={consultantShare} onChange={(e) => setConsultantShare(e.target.value)} placeholder="0" />
              </Field>
              <Field label="Consultant fee per RAMS (£)">
                <NumberInput min={0} step={10} value={consultantFee} onChange={(e) => setConsultantFee(e.target.value)} placeholder="150" />
              </Field>
            </div>
          </div>

          <div className="rounded-xl border border-line bg-mist/60 p-4">
            <span className="text-[0.8rem] font-bold uppercase tracking-wide text-muted">Send-backs</span>
            <p className="mt-1 text-[0.78rem] text-muted">When a principal contractor returns a RAMS for changes — count the delay and chasing, not just the rewrite.</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="% of submissions sent back">
                <NumberInput min={0} max={100} step={5} value={sendBackRate} onChange={(e) => setSendBackRate(e.target.value)} placeholder="20" />
              </Field>
              <Field label="Hours lost per send-back">
                <NumberInput min={0} step={0.5} value={sendBackHours} onChange={(e) => setSendBackHours(e.target.value)} placeholder="4" />
              </Field>
            </div>
          </div>

          <div className="rounded-xl border border-line bg-mist/60 p-4">
            <span className="text-[0.8rem] font-bold uppercase tracking-wide text-muted">Comparison assumptions</span>
            <p className="mt-1 text-[0.78rem] text-muted">Used for the side-by-side table on the right. Both are editable — change them to your own figures.</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="Consultant fee per RAMS (£)" hint="A typical UK figure is £250–£500 per document.">
                <NumberInput min={0} step={25} value={compareFee} onChange={(e) => setCompareFee(e.target.value)} placeholder="350" />
              </Field>
              <Field label="Minutes to review a RamsDocs draft" hint="Time for you to check and sign off a generated draft.">
                <NumberInput min={0} step={5} value={reviewMins} onChange={(e) => setReviewMins(e.target.value)} placeholder="15" />
              </Field>
            </div>
          </div>
        </div>
      </div>

      {/* Result + printable breakdown */}
      <div className="space-y-4">
        <div className="print-area card shadow-soft p-6" id="rams-cost-record">
          <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
            What your RAMS paperwork costs
          </div>
          <h2 className="mt-1 text-lg">
            {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
          </h2>

          <div className="mt-4 rounded-xl border border-line bg-emerald-tint px-5 py-4">
            <div className="text-[0.78rem] font-semibold uppercase tracking-wide text-emerald-deep">Total per year</div>
            <div className="mt-0.5 font-display text-3xl font-bold text-ink">{GBP.format(c.totalCost)}</div>
            <div className="mt-1 text-[0.9rem] text-emerald-deep">
              {c.volume > 0 ? (
                <>about {GBP.format(c.perRams)} per RAMS, across {HOURS.format(c.volume)} a year</>
              ) : (
                <>enter your numbers to see the cost</>
              )}
            </div>
          </div>

          <table className="mt-4 w-full text-sm">
            <tbody>
              <tr className="border-b border-line/60">
                <td className="py-2 pr-2 text-ink">Hours on RAMS paperwork</td>
                <td className="py-2 text-right font-semibold text-ink">{HOURS.format(Math.round(c.totalHours))} h</td>
              </tr>
              <tr className="border-b border-line/60">
                <td className="py-2 pr-2 text-ink">Cost of your time writing them</td>
                <td className="py-2 text-right font-semibold text-ink">{GBP.format(c.timeCost)}</td>
              </tr>
              {c.hasConsultant && (
                <tr className="border-b border-line/60">
                  <td className="py-2 pr-2 text-ink">Consultant fees</td>
                  <td className="py-2 text-right font-semibold text-ink">{GBP.format(c.consultantCost)}</td>
                </tr>
              )}
              {c.hasSendBacks && (
                <tr className="border-b border-line/60">
                  <td className="py-2 pr-2 text-ink">
                    Send-backs <span className="text-muted">({HOURS.format(Math.round(c.sendBackCount))}/yr)</span>
                  </td>
                  <td className="py-2 text-right font-semibold text-ink">{GBP.format(c.sendBackCost)}</td>
                </tr>
              )}
              <tr>
                <td className="py-2 pr-2 font-semibold text-ink">Total annual cost</td>
                <td className="py-2 text-right font-display text-lg font-bold text-ink">{GBP.format(c.totalCost)}</td>
              </tr>
            </tbody>
          </table>

          {c.volume > 0 && (
            <div className="mt-6 avoid-break">
              <h3 className="font-display text-[0.95rem] font-bold uppercase tracking-wide text-ink">
                Three ways to produce the same {HOURS.format(c.volume)} RAMS a year
              </h3>
              <TableScroll>
                <table className="mt-2 w-full min-w-[440px] text-sm">
                  <thead>
                    <tr className="border-b border-line text-left text-[0.72rem] uppercase tracking-wide text-muted">
                      <th className="py-1.5 pr-2 font-semibold">Per year</th>
                      <th className="py-1.5 pr-2 text-right font-semibold">Doing it yourself</th>
                      <th className="py-1.5 pr-2 text-right font-semibold">Consultant</th>
                      <th className="py-1.5 text-right font-semibold">RamsDocs</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-line/60">
                      <td className="py-2 pr-2 text-ink">Your hours</td>
                      <td className="py-2 pr-2 text-right text-ink">{HOURS.format(Math.round(c.scenarios.diy.hours))} h</td>
                      <td className="py-2 pr-2 text-right text-ink">{HOURS.format(Math.round(c.scenarios.consultant.hours))} h</td>
                      <td className="py-2 text-right font-semibold text-emerald-deep">{HOURS.format(Math.round(c.scenarios.ramsdocs.hours))} h</td>
                    </tr>
                    <tr className="border-b border-line/60">
                      <td className="py-2 pr-2 text-ink">Cost</td>
                      <td className="py-2 pr-2 text-right text-ink">{GBP.format(c.scenarios.diy.cost)}</td>
                      <td className="py-2 pr-2 text-right text-ink">{GBP.format(c.scenarios.consultant.cost)}</td>
                      <td className="py-2 text-right font-semibold text-emerald-deep">{GBP.format(c.scenarios.ramsdocs.cost)}</td>
                    </tr>
                    {c.hasSendBacks && (
                      <tr>
                        <td className="py-2 pr-2 text-muted">A rejected RAMS costs you</td>
                        <td className="py-2 pr-2 text-right text-muted" colSpan={3}>
                          {GBP.format(num(sendBackHours) * c.rate)} each ({GBP.format(c.sbCostPerYear)}/yr) — the same on any route
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </TableScroll>

              <p className="mt-3 text-[0.9rem] leading-relaxed text-body">
                {c.saveVsConsultant > 0 ? (
                  <>Compared with a consultant at {GBP.format(num(compareFee))}/RAMS, RamsDocs would save you about{" "}
                  <span className="font-semibold text-ink">{GBP.format(c.saveVsConsultant)}</span> and{" "}
                  <span className="font-semibold text-ink">{HOURS.format(Math.round(c.hoursVsConsultant))} hours</span> a year.</>
                ) : (
                  <>On these figures a consultant works out cheaper than your own time — adjust the fee or your hourly rate to compare your real options.</>
                )}
              </p>

              <p className="mt-2 text-[0.78rem] leading-relaxed text-muted">
                This comparison uses your own figures plus typical UK market rates — change any of them to match your situation.
                RamsDocs is £0 during early access; the only cost shown is the time to review each draft. Whichever route you
                pick, the document is only as good as the competent-person review behind it — this tool informs that decision, it
                doesn&apos;t certify the result.
              </p>
            </div>
          )}

          <PrintFooter toolName="RAMS cost calculator" />
        </div>

        {/* Honest RamsDocs context — no fabricated savings claim. */}
        <div className="card border-emerald/30 bg-emerald-tint/50 p-5 print-hide">
          <div className="font-display text-base font-semibold text-ink">RamsDocs is free during early access</div>
          <p className="mt-1.5 text-sm leading-relaxed text-body">
            That&apos;s {GBP.format(c.totalCost)} a year on your current process. RamsDocs costs £0 right now — no
            card, no subscription. If it saves you even half the time you spend writing each RAMS, you can do the
            arithmetic from your own figures above.
          </p>
          <Link
            href="/builder/"
            className="mt-3 inline-flex items-center justify-center rounded-full bg-emerald px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-deep"
          >
            Build a RAMS draft
          </Link>
        </div>

        <div className="flex justify-end">
          <PrintButton tool="rams-cost-calculator" label="Print / save summary as PDF" />
        </div>
      </div>
    </div>
  );
}
